'use client';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import noop from 'lodash/noop';
import useTranslation from '@components/Translation/useTranslation';
import { ConnectedFocusError } from 'focus-formik-error';

import { useLogin } from '@hooks/auth';
import Button from '@repo/ui/src/components/Button';
import { ErrorState, Input, Checkbox } from '@components/Form';

import { AuthViews, initialValues, InputKeys } from '@repo/ui/src/modules/UI/constants';
import Markdown from '@repo/ui/src/components/Markdown';

interface LoginProps {
  onSuccess?: () => Promise<void>;
  setView?: (view: AuthViews) => void;
  isCheckout?: boolean;
}

const LoginCard = ({ setView, onSuccess = noop as any, isCheckout = false }: LoginProps) => {
  const { t } = useTranslation();
  const { trigger } = useLogin();

  const onSubmit = async (
    values: typeof initialValues.logIn,
    { setStatus, resetForm }: FormikHelpers<typeof initialValues.logIn>,
  ) => {
    setStatus(null);
    try {
      await trigger(values);
      await onSuccess();
      resetForm();
    } catch (e: any) {
      setStatus(e?.response?.data?.message);
    }
  };

  const validationSchema = () =>
    Yup.object().shape({
      [InputKeys.EMAIL]: Yup.string()
        .email(t('auth.fields.emailInput.invalidText', undefined, { default: 'Invalid email' }))
        .required(
          t('auth.fields.emailInput.requiredText', undefined, { default: 'Email is required' }),
        ),
      [InputKeys.PASSWORD]: Yup.string().required(
        t('auth.fields.passwordInput.requireText', undefined, { default: 'Password is required' }),
      ),
    });
  const legalDisclaimer = t('auth.logIn.footer.legalDisclaimer', undefined, {
    default: 'none',
  });
  return (
    <Formik
      validationSchema={validationSchema}
      validateOnChange={false}
      onSubmit={onSubmit}
      initialValues={initialValues.logIn}
    >
      {({ isSubmitting, status }) => (
        <Form className="tw-mb-7 tw-grid tw-gap-y-5 sm:tw-gap-y-7">
          <ConnectedFocusError />
          <Input
            autoComplete="email"
            id={InputKeys.EMAIL}
            name={InputKeys.EMAIL}
            type="email"
            required
            label={t('auth.fields.emailInput.label', undefined, {
              default: 'Email',
            })}
          />
          <Input
            id="password"
            name="password"
            type="password"
            required
            label={t('auth.fields.passwordInput.label', undefined, {
              default: 'Password',
            })}
          />
          <div className="tw-flex tw-justify-between tw-items-center">
            <Checkbox
              id="rememberMe"
              name="rememberMe"
              label={t('auth.logIn.form.rememberMeLabel', undefined, {
                default: 'Remember Me',
              })}
            />

            <button
              className="tra-text-web-body-m-link"
              tabIndex={0}
              type="button"
              onClick={() => setView?.(AuthViews.FORGOT)}
            >
              {t('auth.logIn.form.forgotPasswordLabel', undefined, {
                default: 'Forgot Password',
              })}
            </button>
          </div>
          <ErrorState text={status} />
          {legalDisclaimer !== 'none' && (
            <Markdown>
              {t('auth.logIn.footer.legalDisclaimer', undefined, { default: 'none' })}
            </Markdown>
          )}
          <Button size="large" disabled={isSubmitting} type="submit" full>
            {isCheckout
              ? t('auth.logIn.form.checkoutButtonText', undefined, {
                  default: 'Log In and Checkout',
                })
              : t('auth.logIn.form.buttonText', undefined, { default: 'Log In' })}
          </Button>
        </Form>
      )}
    </Formik>
  );
};
export default LoginCard;
